import { useCallback } from 'react'
import { useCreateChat } from '../../../hooks/queries'
import { useAuthStore } from '../../../store/authStore'
import type { Chat } from '../../../app/modules/types/chatBot'

/**
 * Bind a chat into the store `ChatInner` reads its identity from.
 *
 * The map is keyed by finding id for an anchored chat; a free chat has no
 * finding, so it keys on its OWN id. That is not a new convention —
 * `ChatHistoryPage` has always done this for the free chats it lists — and
 * `ChatInner` only ever reads the key back as a map key. (It also has to be
 * non-empty: `SetAiAssistantVulnerabilitiesID` silently discards a payload
 * whose `vulID` is falsy.)
 */
function bindChat(chat: Chat) {
  useAuthStore.getState().SetSelectedVulnId(chat.id)
  useAuthStore.getState().SetAiAssistantVulnerabilitiesID({
    vulID: chat.id,
    id: chat.id,
    title: chat.title,
  })
}

/**
 * Creating and opening a chat with no vulnerability behind it.
 *
 * Shared by the three places that can start one — the drawer header, the
 * chat's own start screen, and Chat History — so the store convention above
 * lives in exactly one file.
 */
export function useFreeChat() {
  const createChat = useCreateChat()

  /**
   * `mutate(undefined)` is `POST /chat {}`: no subject. `useCreateChat`'s
   * subject cache write-through is already conditional on both subject fields,
   * so it correctly does nothing for one of these.
   *
   * Always driven by an explicit click — never by merely opening the drawer —
   * so an idle visit leaves no orphan row in the org's history.
   */
  const startNewChat = useCallback(
    (onCreated?: (chat: Chat) => void) => {
      createChat.mutate(undefined, {
        onSuccess: (chat) => {
          bindChat(chat)
          onCreated?.(chat)
        },
      })
    },
    [createChat],
  )

  return { startNewChat, bindChat, isCreating: createChat.isPending }
}
